import React, { useState, useEffect } from 'react';
import { X, Ship, AlertCircle, Shield, Home, Settings } from 'lucide-react';
import { useVessel } from '../../contexts/VesselContext';
import { useCustomDialogs } from '../../hooks/useCustomDialogs';
import { azureApiClient } from '../../lib/azureApiClient';
import BasicInfoTab from './settings/tabs/BasicInfoTab';
import OperationsTab from './settings/tabs/OperationsTab';
import SafetyEquipmentTab from './settings/tabs/SafetyEquipmentTab';
import VesselSpacesTab from './settings/tabs/VesselSpacesTab';
import SystemsTab from './settings/tabs/SystemsTab';

const AddVesselModal = ({ isOpen, onClose }) => {
  const { addVessel, setCurrentVessel } = useVessel();
  const { showAlert, AlertDialog } = useCustomDialogs();
  const [activeTab, setActiveTab] = useState('basic');
  const [vesselConfig, setVesselConfig] = useState({
    // Basic vessel info
    vesselName: '',
    vesselType: '',
    category: '',
    lengthOverall: '',
    grossTonnage: '',
    draft: '',
    enginePower: '',
    
    // Capacity
    maxPersons: '',
    passengerCapacity: '',
    crewCount: 3,
    
    // Operating status for HOR compliance
    operatingStatus: 'seagoing', // Default to seagoing (more restrictive)
    
    // Key equipment for conditional logic
    hasLiferaft: false,
    hasEPIRB: false,
    hasSART: false,
    hasAccommodation: false,
    hasGalley: false,
    hasEnclosedSpaces: false,
    hasEmergencySteering: false,
    
    // Life Saving Appliances
    liferafts_fitted: false,
    liferaft_capacity: '',
    lifeboat_fitted: false,
    rescue_boat_fitted: false,
    
    // Additional equipment flags
    power_critical_systems: false,
    towing_capable: false,
    bilge_auto: false,
    engineer_onboard: false,
    enclosed_accommodation_present: false,
    cabin_accommodation: false,
    GMDSS_equipped: false,
    
    // Operations & Equipment Arrays
    operations: [],      // ['dangerous_goods', 'towing_operations', 'survey_operations']
    equipment: [],       // ['winch_lifting_gear', 'gas_detection', 'grab_bag', 'fixed_firefighting']
    extra_equipment: [], // ['sart', 'epirb_extra', 'immersion_suits']
    
    // Environmental
    oilCapacityLitres: '',
    medical_stores_category: 'none',
    
    // Operations
    operatingHours: 'under_24h', // 'under_24h' or 'over_24h'
    internationalVoyages: false,
    
    // Equipment Check Settings
    equipmentCheckSettings: {
      lsaFrequency: 30,
      ffeFrequency: 30
    }
  });

  const updateConfig = (updates) => {
    setVesselConfig(prev => ({ ...prev, ...updates }));
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSave = async () => {
    if (!vesselConfig.vesselName?.trim()) {
      await showAlert('Please enter a vessel name');
      return;
    }

    try {
      // Step 1: Register vessel in Azure (gets UUID back)
      // This requires admin role - will fail with 403 for regular users
      const registeredVessel = await azureApiClient.registerVessel({
        name: vesselConfig.vesselName,
        vesselType: vesselConfig.vesselType,
        imo: vesselConfig.imo,
        flag: vesselConfig.flag || 'GB',
        gt: vesselConfig.grossTonnage
      });

      // Step 2: Add to local storage with the UUID from Azure
      const vesselData = {
        id: registeredVessel.id,  // UUID from Azure!
        name: vesselConfig.vesselName,
        type: vesselConfig.vesselType,
        category: vesselConfig.category,
        port: vesselConfig.homePort || '',
        imo: vesselConfig.imo || '',
        flag: vesselConfig.flag || 'GB',
        operatingStatus: vesselConfig.operatingStatus,
        hasPassengers: vesselConfig.passengerCapacity > 0,
        maxPassengers: parseInt(vesselConfig.passengerCapacity) || 0,
        ...vesselConfig
      };

      // Add the new vessel with server-generated UUID
      const newVessel = addVessel(vesselData);

      // Switch to the new vessel
      setCurrentVessel(newVessel.id);

      onClose();

    } catch (error) {
      console.error('[AddVesselModal] Registration failed:', error);
      // Show user-friendly error message
      if (error.message.includes('403') || error.message.includes('Admin')) {
        await showAlert('Only administrators can create new vessels. Please contact your organization admin.');
      } else {
        await showAlert(error.message || 'Failed to create vessel. Please check your connection and try again.');
      }
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic', icon: Ship },
    { id: 'operations', label: 'Operations', icon: AlertCircle },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'spaces', label: 'Spaces', icon: Home },
    { id: 'systems', label: 'Systems', icon: Settings }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-2 sm:p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Add New Vessel</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">Configure your new vessel details for compliance calculations</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto flex-shrink-0">
            <nav className="flex px-4 sm:px-6" style={{ paddingBottom: '0' }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group inline-flex items-center py-3 px-3 border-b-2 font-medium text-sm whitespace-nowrap relative
                      ${activeTab === tab.id
                        ? 'border-cyan-500 text-cyan-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                    style={{ marginBottom: '-2px' }}
                  >
                    <Icon className={`
                      mr-1.5 h-4 w-4
                      ${activeTab === tab.id ? 'text-cyan-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {activeTab === 'basic' && <BasicInfoTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'operations' && <OperationsTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'safety' && <SafetyEquipmentTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'spaces' && <VesselSpacesTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'systems' && <SystemsTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}

            {/* Actions - inside content to ensure visibility */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  * Required fields
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                    onClick={handleSave}
                  >
                    Add Vessel
                  </button>
                </div>
              </div>
            </div>

            {/* Extra padding to ensure scrollability */}
            <div className="h-20"></div>
          </div>
        </div>
      </div>
      
      {/* Custom Dialogs */}
      <AlertDialog />
    </div>
  );
};

export default AddVesselModal;